import { ElementType, HTMLAttributes, KeyboardEvent, MouseEvent, ReactElement, ReactNode, cloneElement, useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { usePopover } from '../../../hooks/usePopover';
import { cn } from '../../../utils/cn';
import { Icon } from '../Icon/Icon';
import { Portal } from '../Portal/Portal';

/** Vertical list of actions or links. `tone="dark"` is used on the navy sidebar. */
type Tone = 'light' | 'dark';

export interface MenuItemSpec {
  label?: ReactNode;
  icon?: string;
  description?: ReactNode;
  onClick?: () => void;
  to?: string;
  danger?: boolean;
  disabled?: boolean;
  badge?: ReactNode;
  divider?: boolean;
  /** Section label shown above the next items */
  heading?: ReactNode;
}

export function Menu({ children, className, tone = 'light', role, ...rest }: HTMLAttributes<HTMLDivElement> & { tone?: Tone }) {
  return (
    <div role={role} data-tone={tone} className={cn('flex flex-col gap-0.5', className)} {...rest}>
      {children}
    </div>
  );
}

const TONES: Record<Tone, any> = {
  light: {
    base: 'text-navy-900 hover:bg-navy-50 focus-visible:bg-navy-50',
    active: 'bg-navy-50 font-semibold text-navy-800',
    icon: 'text-ink-400',
    activeIcon: 'text-brand-600',
  },
  dark: {
    base: 'text-white/70 hover:bg-white/[0.06] hover:text-white focus-visible:bg-white/10',
    active: 'bg-white/[0.09] font-medium text-white',
    icon: 'text-white/60',
    activeIcon: 'text-white',
  },
};

/**
 * One row. Renders a <button> by default; pass `as={Link}` + `to` for links.
 * `active` highlights the row. `collapsed` (sidebar rail) shows only the icon.
 */
export interface MenuItemProps extends Record<string, any> {
  as?: ElementType;
  icon?: string;
  children?: ReactNode;
  description?: ReactNode;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  badge?: ReactNode;
  tone?: Tone;
  collapsed?: boolean;
  className?: string;
  role?: string;
}

export function MenuItem({ as: Comp = 'button', icon, children, description, active, danger, disabled, badge, tone = 'light', collapsed, className, role, ...rest }: MenuItemProps) {
  const t = TONES[tone];
  const extra = Comp === 'button' ? { type: 'button', disabled } : {};
  return (
    <Comp
      role={role}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      title={collapsed && typeof children === 'string' ? children : undefined}
      className={cn(
        'relative flex min-h-[38px] w-full items-center gap-3 rounded-lg px-3 py-1.5 text-left text-sm outline-none transition-colors',
        collapsed && 'justify-center px-0',
        'focus-visible:ring-2 focus-visible:ring-steel/60',
        active ? t.active : t.base,
        danger && 'text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:bg-red-50',
        disabled && 'pointer-events-none opacity-40',
        className,
      )}
      {...extra}
      {...rest}
    >
      {icon && <Icon name={icon} size={19} className={cn('shrink-0', danger ? 'text-red-500' : active ? t.activeIcon : t.icon)} />}
      <span className={cn('min-w-0 flex-1', collapsed && 'sr-only')}>
        <span className="block truncate">{children}</span>
        {description && <span className="block truncate text-[13px] font-normal text-ink-400">{description}</span>}
      </span>
      {badge != null && (
        <span
          className={cn(
            'rounded-full px-1.5 py-px text-[11px] font-semibold tabular',
            tone === 'dark' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600',
            collapsed && 'absolute right-1.5 top-1',
          )}
        >
          {badge}
        </span>
      )}
    </Comp>
  );
}

export function MenuDivider({ tone = 'light' }: { tone?: Tone }) {
  return <div role="separator" className={cn('my-1 h-px', tone === 'dark' ? 'bg-white/10' : 'bg-line')} />;
}

export function MenuLabel({ children, tone = 'light' }: { children?: ReactNode; tone?: Tone }) {
  return <div className={cn('px-3 pb-1.5 pt-4 text-[11px] font-medium uppercase tracking-[0.08em]', tone === 'dark' ? 'text-white/40' : 'text-ink-400')}>{children}</div>;
}

/**
 * Button that opens a menu of actions.
 * items: [{ label, icon, onClick, danger, disabled, description } | { divider: true } | { heading }]
 */
export interface MenuDropdownProps {
  /** A button element; the menu adds its own click and keyboard handlers. */
  trigger: ReactElement;
  /** Falsy entries are skipped, so items can be written as `cond && { … }`. */
  items?: (MenuItemSpec | false | null | undefined | '' | 0)[];
  align?: 'start' | 'end';
  width?: number;
  header?: ReactNode;
}

export function MenuDropdown({ trigger, items = [], align = 'end', width = 224, header }: MenuDropdownProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const style = usePopover(open, anchorRef, popRef, { align });
  useClickOutside([anchorRef, popRef], () => setOpen(false), open);

  const focusables = () => [...(popRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])];

  useEffect(() => {
    if (!open) return;
    setTimeout(() => popRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus(), 0);
  }, [open]);

  const close = () => {
    setOpen(false);
    anchorRef.current?.querySelector('button')?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const els = focusables();
    const i = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[(i + 1) % els.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      els[(i - 1 + els.length) % els.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      els[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      els[els.length - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        {cloneElement(trigger, {
          'aria-haspopup': 'menu',
          'aria-expanded': open,
          onClick: (e: MouseEvent) => {
            trigger.props.onClick?.(e);
            setOpen((o) => !o);
          },
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              setOpen(true);
            }
          },
        })}
      </span>
      {open && (
        <Portal>
          <div ref={popRef} style={{ ...style, width }} className="rounded-xl border border-line bg-white p-1 shadow-pop animate-pop">
            {header}
            <Menu role="menu" onKeyDown={onKeyDown}>
              {(items.filter(Boolean) as MenuItemSpec[]).map((item, idx) =>
                item.divider ? (
                  <MenuDivider key={`d${idx}`} />
                ) : item.heading ? (
                  <MenuLabel key={`h${idx}`}>{item.heading}</MenuLabel>
                ) : (
                  <MenuItem
                    key={String(item.label ?? idx)}
                    role="menuitem"
                    tabIndex={-1}
                    icon={item.icon}
                    danger={item.danger}
                    disabled={item.disabled}
                    description={item.description}
                    onClick={() => {
                      setOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ),
              )}
            </Menu>
          </div>
        </Portal>
      )}
    </>
  );
}

export default Menu;
